/**
 * Wake-time solver — §4.1. Pure arithmetic over "minutes from the target day's
 * local midnight" so it's trivially testable; a thin layer (instants.ts) maps
 * the result onto absolute Instants for the payload.
 *
 * Times are minutes from local midnight of the wake day. A bedtime the previous
 * evening is negative (23:30 prior night = -30). Travel can push leave-by before
 * the event; routine pushes wake before leave-by.
 *
 * RESOLVED TENSION: the spec gives both a master formula
 *   wake = max(sleep_floor, min(schedule_wake, empty_morning_wake))
 * and a prose "conflict rule": if you went to bed too late, *schedule wins — the
 * system never sleeps through a commitment.* Those disagree when
 * sleep_floor > schedule_wake (the max() would sleep past the commitment). We
 * implement the prose, because sleeping through a hard commitment is the one
 * unacceptable failure. sleep_floor then only ever pushes wake *later* on empty
 * mornings, and on committed mornings it just flags the short-sleep conflict.
 */
/** Routine + sleep priors (§4.1 "Routine priors", "Empty-morning rule"). */
export const SOLVER_DEFAULTS = {
    /** Comfortable morning routine; prior before learning (P2). */
    routineComfortMin: 90,
    /** Hard-wake minimum routine — "at least an hour". 30-min runway vs comfort. */
    routineMinimumMin: 60,
    /** Sleep target floor; bedtime + this = earliest "rested" wake. */
    sleepNeedMin: 8 * 60,
    /** Empty-morning sleep-relative wake: bedtime + 8.5h. */
    emptyMorningSleepMin: 8.5 * 60,
    /** Empty-morning fallback when bedtime unknown: 9:00 AM. */
    emptyMorningFallbackMin: 9 * 60,
    /** Latest the day may start on an empty morning: 11:00 AM clamp. */
    emptyMorningLatestMin: 11 * 60,
};
/** Format minutes-from-midnight as "H:MM" (no leading zero) — for human copy. */
export function formatMin(min) {
    // Wrap into [0, 1440) for display; the day boundary is only for arithmetic.
    const wrapped = ((min % 1440) + 1440) % 1440;
    const h = Math.floor(wrapped / 60);
    const m = wrapped % 60;
    return `${h}:${m.toString().padStart(2, "0")}`;
}
/** Format minutes-from-midnight as canonical zero-padded "HH:MM" (contract data). */
export function formatHHMM(min) {
    const wrapped = ((min % 1440) + 1440) % 1440;
    const h = Math.floor(wrapped / 60);
    const m = wrapped % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}
export function solveWake(input) {
    const d = SOLVER_DEFAULTS;
    const routineComfort = input.routineComfortMin ?? d.routineComfortMin;
    const routineMinimum = input.routineMinimumMin ?? d.routineMinimumMin;
    const sleepNeed = input.sleepNeedMin ?? d.sleepNeedMin;
    const sleepFloorMin = input.bedtimeMin === undefined ? null : input.bedtimeMin + sleepNeed;
    if (input.firstEvent) {
        const { startMin, travelMin, parkingBufferMin } = input.firstEvent;
        const leaveByMin = startMin - travelMin - parkingBufferMin;
        const scheduleWakeMin = leaveByMin - routineComfort;
        const hardWakeMin = leaveByMin - routineMinimum;
        // Schedule wins, always. sleep_floor only flags a short-sleep conflict.
        const wakeMin = scheduleWakeMin;
        const conflict = sleepFloorMin !== null && sleepFloorMin > scheduleWakeMin;
        const explanation = `${formatMin(wakeMin)} = ${formatMin(leaveByMin)} leave-by ` +
            `− ${travelMin}m drive − ${parkingBufferMin}m parking − ${routineComfort}m routine`;
        let note = null;
        if (conflict) {
            const hours = sleepFloorMin !== null ? (scheduleWakeMin - input.bedtimeMin) / 60 : 0;
            const rounded = Math.max(0, Math.round(hours));
            note = `You got about ${rounded} hours; today's going to cost you. Coffee first.`;
        }
        return {
            wakeMin,
            hardWakeMin,
            leaveByMin,
            scheduleWakeMin,
            sleepFloorMin,
            conflict,
            emptyMorning: false,
            explanation,
            note,
        };
    }
    // Empty morning — sleep-relative, clamped to a latest bound (night-owl aware).
    const emptySleep = input.emptyMorningSleepMin ?? d.emptyMorningSleepMin;
    const fallback = input.emptyMorningFallbackMin ?? d.emptyMorningFallbackMin;
    const latest = input.emptyMorningLatestMin ?? d.emptyMorningLatestMin;
    const base = input.bedtimeMin === undefined ? fallback : input.bedtimeMin + emptySleep;
    const wakeMin = Math.min(base, latest);
    // No commitment, so Hard Wake is just the same 30-min runway past wake.
    const runway = routineComfort - routineMinimum;
    const hardWakeMin = wakeMin + runway;
    const explanation = input.bedtimeMin === undefined
        ? `${formatMin(wakeMin)} = empty morning, no sleep data → 9:00 fallback`
        : `${formatMin(wakeMin)} = bedtime ${formatMin(input.bedtimeMin)} + 8.5h` +
            (base > latest ? ` (clamped to ${formatMin(latest)})` : "");
    return {
        wakeMin,
        hardWakeMin,
        leaveByMin: null,
        scheduleWakeMin: null,
        sleepFloorMin,
        conflict: false,
        emptyMorning: true,
        explanation,
        note: null,
    };
}
//# sourceMappingURL=solver.js.map